/**
 * Reusable factories for `filterHeaders` entries used by CustomizableList.
 *
 * The autocomplete inputType is wired in
 * `src/components/core/complex-filter/ComplexFilterRow.js`. These factories
 * just bundle the query def + label so callers don't repeat the config in
 * every list page.
 */
import { stringOperators } from 'src/constants/TableConstants';
import { getCompanyNameOptions } from 'src/api/queries';

/**
 * Filter that resolves to a Company name via icontains autocomplete.
 *
 * Use this for any FK chain that ultimately points at `Company.name` —
 * `owner__name`, `parent__name`, `company__name`, `device__owner__name`
 * (and the rest). The same backing query covers administrative companies
 * and merchants because both live in the `Company` table; the dropdown
 * shows a "Company" / "Merchant" sub-label on each option so users can
 * tell them apart at a glance.
 *
 * Optional `kind`:
 *   - 'company'  → restrict the dropdown to administrative companies
 *   - 'merchant' → restrict it to merchants
 *   - omitted    → show both, with a per-row sub-label
 */
export const companyAutocompleteFilter = (machineName, humanName, { kind } = {}) => {
  const queryArgs = {};
  if (kind === 'company') queryArgs.parentNull = true;
  if (kind === 'merchant') queryArgs.parentNull = false;
  return {
    machineName,
    humanName,
    operators: stringOperators,
    inputType: 'autocomplete',
    autocomplete: {
      queryDef: getCompanyNameOptions,
      queryArgs,
      optionLabel: (opt) => (typeof opt === 'string' ? opt : opt?.name ?? ''),
      optionSecondary: (opt) => (typeof opt === 'string' ? '' : opt?.kind ?? ''),
      optionValue: (opt) => (typeof opt === 'string' ? opt : opt?.name ?? ''),
    },
  };
};
